import React from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Wifi, 
  Laptop, 
  Building, 
  GraduationCap, 
  Heart, 
  Car, 
  Calculator,
  TrendingUp
} from "lucide-react";
import { FormData } from './types';
import { formatNumber } from './utils';

interface ExpensesStepProps {
  formData: FormData;
  formErrors: Record<string, string>;
  onInputChange: (field: string, value: string, category: 'expenses') => void;
  onNext: () => void;
  calculateTotalMonthlyExpenses: () => number;
}

export const ExpensesStep = ({
  formData,
  formErrors,
  onInputChange,
  onNext,
  calculateTotalMonthlyExpenses,
}: ExpensesStepProps) => {
  const expenseFields = [
    { 
      id: 'internetPhone', 
      label: 'How much are you spending on internet and phone?',
      icon: Wifi,
      color: 'text-accent-blue'
    },
    { 
      id: 'techSoftware', 
      label: 'What about tech/software?',
      icon: Laptop,
      color: 'text-accent-cyan'
    },
    { 
      id: 'workspaceOffice', 
      label: 'How much for the office?',
      icon: Building,
      color: 'text-accent-orange'
    },
    { 
      id: 'professionalDev', 
      label: 'And for professional development?',
      icon: GraduationCap,
      color: 'text-accent-green'
    },
    { 
      id: 'healthcare', 
      label: 'And that monthly health premium?',
      icon: Heart,
      color: 'text-accent-pink'
    },
    { 
      id: 'transportation', 
      label: 'What about getting around?',
      icon: Car,
      color: 'text-accent-yellow'
    },
  ];

  const totalExpenses = calculateTotalMonthlyExpenses();

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-accent-orange/20 to-accent-pink/20 mb-6 animate-scale-in">
          <Calculator className="w-8 h-8 text-accent-orange" />
        </div>
        <h2 className="text-3xl font-bold mb-3 gradient-text">Reality Check</h2>
        <p className="text-text-secondary text-lg leading-relaxed max-w-md mx-auto">
          Let's account for your monthly expenses to get the full picture
        </p>
      </div>

      <div className="grid gap-6">
        {expenseFields.map(({ id, label, icon: Icon, color }, index) => (
          <div key={id} className="relative group animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
            <label htmlFor={id} className="block text-base font-medium text-text-primary mb-3 flex items-center gap-3">
              <Icon className={`w-5 h-5 ${color}`} />
              {label}
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary font-semibold">
                $
              </div>
              <Input
                id={id}
                type="text"
                value={formData.expenses[id]}
                onChange={(e) => onInputChange(id, e.target.value, 'expenses')}
                className="pl-10 pr-4 py-3 text-lg bg-card-bg border-2 border-border hover:border-primary/50 focus:border-primary transition-all duration-300 rounded-xl"
                placeholder="Enter amount"
              />
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 opacity-0 group-focus-within:opacity-100 transition-opacity duration-300 pointer-events-none" />
            </div>
            {formErrors[id] && (
              <p className="text-error mt-2 text-sm flex items-center gap-1 animate-fade-in">
                <span className="w-1 h-1 bg-error rounded-full"></span>
                {formErrors[id]}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Enhanced Total Display */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl blur-xl"></div>
        <div className="relative p-6 bg-gradient-to-br from-card-bg to-card-hover rounded-2xl border border-primary/20 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-text-secondary text-sm">Total Monthly Expenses</p>
                <p className="text-2xl font-bold text-text-primary">
                  ${formatNumber(totalExpenses.toString())}
                </p>
              </div>
            </div>
            <Badge 
              variant="secondary" 
              className="bg-gradient-to-r from-primary/20 to-secondary/20 text-text-primary border-primary/30"
            >
              {totalExpenses > 0 ? 'Looking good!' : 'Add expenses above'}
            </Badge>
          </div>
        </div>
      </div>

      <div className="pt-6">
        <Button
          onClick={onNext}
          className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-primary to-secondary hover:from-primary-hover hover:to-secondary-hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] rounded-xl"
        >
          Next: Time Is Money
          <span className="ml-2">→</span>
        </Button>
      </div>
    </div>
  );
};